import { getShortUrlParse, createShortUrl } from '@opm/services';
import { ShortIdSessionStorageKey } from '../constants/storageKey';
import { history } from 'umi';
import { DBO_U } from '@op/utils/format';
import { cloneDeep } from 'lodash-es';
import * as CryptoJS from 'crypto-js';
import { objToUrl } from '@op/utils/url';

/**
 * 获取短链接缓存信息
 * @param key
 * @returns
 */
function getStorageShortUrlInfo(key: string) {
  try {
    const info = sessionStorage.getItem(ShortIdSessionStorageKey + key);
    if (info === null) return null;
    return JSON.parse(info);
  } catch (error) {
    return null;
  }
}

/**
 * 保存短链接缓存信息
 * @param key
 * @param value
 */
function saveShortUrlInfo(key: string, value: string) {
  sessionStorage.setItem(ShortIdSessionStorageKey + key, value);
}

/**
 * 获取短链接信息
 * 优先从缓存中获取，如果没有，则从服务器获取，并保存到缓存中
 * @param shortId
 * @returns
 */
async function getShortInfo(shortId: string) {
  const data = getStorageShortUrlInfo(shortId);
  if (data !== null) return data;

  const filter = await getShortUrlParse(shortId);
  saveShortUrlInfo(shortId, JSON.stringify(filter));

  return filter;
}

export async function getShortUrl(shortid?: string, defaultValue: any = {}) {
  const { query } = history.location || {};
  const id = shortid || query?.shortid;
  if (!id || typeof id !== 'string') return null;

  const [shortId, sign] = id.split('-');

  const data = await getShortInfo(shortId);

  // 去掉时间参数
  if (sign === '1') {
    const { start_time, end_time, date_type, ...otherInfo } = data;
    if (defaultValue?.start_time) {
      const { start_time: localStartTime, end_time: localEndTime } =
        defaultValue;
      otherInfo.start_time = localStartTime;
      otherInfo.end_time = localEndTime;
    }
    return otherInfo;
  }

  return data;
}

/** 缓存上一次的 filter 内容指纹与生成的 shortid，避免相同内容重复生成 */
let lastContentHash: string | null = null;
let lastShortIdResult: {
  shortId?: string;
  urlParams?: string;
  pathname?: string;
  params?: string;
  cached?: boolean;
} | null = null;

export function createShortId(filter: any) {
  try {
    const { href, pathname } = window.location;
    const dataMap = DBO_U.queryObj(href);

    // 仅对 filter 原始内容计算指纹，用于判断是否变化
    const contentText = JSON.stringify(filter);
    const contentHash = CryptoJS.MD5(contentText).toString();

    if (lastContentHash === contentHash && lastShortIdResult) {
      return { ...lastShortIdResult, cached: true };
    }

    let newFilter = cloneDeep(filter);
    newFilter = {
      ...newFilter,
      createTime: Date.now(),
      salt: Math.random(),
    };
    const text = JSON.stringify(newFilter);
    const md5 = CryptoJS.MD5(text).toString();

    const urlParams = objToUrl({ ...dataMap, shortid: md5 });

    const result = {
      shortId: md5,
      urlParams,
      pathname,
      params: JSON.stringify(filter),
      cached: false,
    };

    lastContentHash = contentHash;
    lastShortIdResult = result;

    return result;
  } catch (error) {
    return {};
  }
}

export function createShortIdAndSetUrl(filter: any) {
  const { shortId, urlParams, pathname, params, cached } = createShortId(filter) as any;
  if (shortId) {
    // 缓存命中时跳过服务端写入，避免重复调用
    if (!cached) {
      createShortUrl({ short_url: shortId, params }).catch(() => {});
    }
    window.history.replaceState(null, '', `${pathname}?${urlParams}`);
    return shortId;
  }
}
